import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AccessTime,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Flag,
  Psychology,
  RestartAlt,
  School,
} from "@mui/icons-material";

import {
  CLAUDE_EXAM_DOMAINS,
} from "../data/claudePracticeDomains";
import {
  DEFAULT_CLAUDE_PRACTICE_SETTINGS,
  normalizeClaudePracticeSettings,
  selectClaudePracticeQuestionIds,
} from "../data/claudePracticeConfig";
import {
  flushPendingExamSimulationSubmissions,
  loadExamSimulationQuestions,
  queueExamSimulationSubmission,
  startExamSimulationAttempt,
  submitExamSimulationAttempt,
  trackExamSimulationVisit,
} from "../utils/examSimulationApi";
import "../styles/claude-practice-test.css";

const DRAFT_KEY = "ucClaudePracticeDraft";
const HISTORY_KEY = "ucClaudePracticeHistory";
const EXAM_SIMULATION_SLUG = "claude-architect-foundations";

const sameAnswers = (selected = [], correct = []) => {
  if (selected.length !== correct.length) return false;
  const selectedSet = new Set(selected);
  return correct.every((answer) => selectedSet.has(answer));
};

const formatTime = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainder = safeSeconds % 60;
  return [hours, minutes, remainder].map((part) => String(part).padStart(2, "0")).join(":");
};

const buildResult = ({
  questionIds,
  questionMap,
  bankVersion,
  answers,
  startedAt,
  submittedAt,
  attemptSettings,
  timedOut = false,
}) => {
  const questions = questionIds.map((id) => questionMap.get(id)).filter(Boolean);
  const correctCount = questions.filter((item) => sameAnswers(answers[item.id] || [], item.correct)).length;
  const domains = Object.entries(CLAUDE_EXAM_DOMAINS).map(([key, domain]) => {
    const domainQuestions = questions.filter((item) => item.domain === key);
    const domainCorrect = domainQuestions.filter((item) =>
      sameAnswers(answers[item.id] || [], item.correct)
    ).length;
    const percentage = domainQuestions.length
      ? Math.round((domainCorrect / domainQuestions.length) * 100)
      : 0;
    return {
      key,
      ...domain,
      correct: domainCorrect,
      total: domainQuestions.length,
      percentage,
    };
  });
  const percentage = questions.length ? Math.round((correctCount / questions.length) * 100) : 0;
  const ready =
    percentage >= attemptSettings.readinessScore &&
    domains.every((domain) => domain.percentage >= attemptSettings.domainMinimum);

  return {
    version: bankVersion,
    questionIds,
    answers,
    correctCount,
    total: questions.length,
    percentage,
    domains,
    ready,
    startedAt,
    submittedAt,
    timedOut,
    readinessScore: attemptSettings.readinessScore,
    domainMinimum: attemptSettings.domainMinimum,
  };
};

const ClaudePracticeTest = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("intro");
  const [questionBank, setQuestionBank] = useState([]);
  const [questionBankVersion, setQuestionBankVersion] = useState("");
  const [questionBankLoaded, setQuestionBankLoaded] = useState(false);
  const [questionBankError, setQuestionBankError] = useState("");
  const [questionIds, setQuestionIds] = useState([]);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_CLAUDE_PRACTICE_SETTINGS);
  const [attemptSettings, setAttemptSettings] = useState(DEFAULT_CLAUDE_PRACTICE_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [attemptId, setAttemptId] = useState("");
  const [startingAttempt, setStartingAttempt] = useState(false);
  const [startError, setStartError] = useState("");
  const [trackingStatus, setTrackingStatus] = useState("");
  const [remainingSeconds, setRemainingSeconds] = useState(
    DEFAULT_CLAUDE_PRACTICE_SETTINGS.timeMinutes * 60
  );
  const [result, setResult] = useState(null);
  const [resumeAvailable, setResumeAvailable] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("all");

  const questionMap = useMemo(
    () => new Map(questionBank.map((item) => [item.id, item])),
    [questionBank]
  );
  const questions = useMemo(
    () => questionIds.map((id) => questionMap.get(id)).filter(Boolean),
    [questionIds, questionMap]
  );
  const activeQuestion = questions[currentIndex];
  const answeredCount = questions.filter((item) => (answers[item.id] || []).length > 0).length;

  useEffect(() => {
    let active = true;
    loadExamSimulationQuestions(EXAM_SIMULATION_SLUG)
      .then((payload) => {
        if (!active) return;
        const nextQuestions = Array.isArray(payload.questions) ? payload.questions : [];
        if (!nextQuestions.length || nextQuestions.length !== Number(payload.questionCount)) {
          throw new Error("The question bank is incomplete.");
        }
        setQuestionBank(nextQuestions);
        setQuestionBankVersion(String(payload.bankVersion || ""));
        setQuestionBankError("");
      })
      .catch((error) => {
        if (active) {
          setQuestionBank([]);
          setQuestionBankVersion("");
          setQuestionBankError(
            error.message || "The practice question bank could not be loaded."
          );
        }
      })
      .finally(() => {
        if (active) setQuestionBankLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/getdynamicdata`)
      .then((response) => {
        if (!response.ok) throw new Error("Settings request failed");
        return response.json();
      })
      .then((rows) => {
        if (active) setSettings(normalizeClaudePracticeSettings(rows));
      })
      .catch(() => {
        // Safe defaults keep the test available if settings cannot be loaded.
      })
      .finally(() => {
        if (active) setSettingsLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    trackExamSimulationVisit(EXAM_SIMULATION_SLUG).catch(() => {
      // The page remains usable if visit analytics are temporarily unavailable.
    });
    flushPendingExamSimulationSubmissions();
  }, []);

  useEffect(() => {
    try {
      const draft = JSON.parse(window.localStorage.getItem(DRAFT_KEY) || "null");
      setResumeAvailable(
        Boolean(
          draft &&
          questionBankLoaded &&
          draft.version === questionBankVersion &&
          Array.isArray(draft.questionIds) &&
          draft.questionIds.length > 0 &&
          draft.questionIds.every((id) => questionMap.has(id)) &&
          draft.endTime > Date.now()
        )
      );
    } catch (_) {
      setResumeAvailable(false);
    }
  }, [questionBankLoaded, questionBankVersion, questionMap]);

  const saveHistory = useCallback((completedResult) => {
    try {
      const current = JSON.parse(window.localStorage.getItem(HISTORY_KEY) || "[]");
      const history = Array.isArray(current) ? current : [];
      window.localStorage.setItem(
        HISTORY_KEY,
        JSON.stringify([
          {
            version: completedResult.version,
            percentage: completedResult.percentage,
            ready: completedResult.ready,
            submittedAt: completedResult.submittedAt,
            domains: completedResult.domains.map(({ key, percentage }) => ({ key, percentage })),
          },
          ...history,
        ].slice(0, 5))
      );
    } catch (_) {
      // Exam completion must not fail because browser storage is unavailable.
    }
  }, []);

  const submitExam = useCallback(
    (timedOut = false, skipConfirmation = false) => {
      if (!questionIds.length) return;
      if (!skipConfirmation && !timedOut) {
        const unanswered = questionIds.length - Object.values(answers).filter((value) => value?.length).length;
        const message = unanswered
          ? `You still have ${unanswered} unanswered question${unanswered === 1 ? "" : "s"}. Submit anyway?`
          : "Submit your UC Claude Practice Test now?";
        if (!window.confirm(message)) return;
      }

      const completedResult = buildResult({
        questionIds,
        questionMap,
        bankVersion: questionBankVersion,
        answers,
        startedAt,
        submittedAt: Date.now(),
        attemptSettings,
        timedOut,
      });
      const serverResult = {
        correctCount: completedResult.correctCount,
        percentage: completedResult.percentage,
        ready: completedResult.ready,
        timedOut: completedResult.timedOut,
        answeredCount: Object.values(answers).filter((value) => value?.length).length,
        flaggedCount: flagged.length,
        domains: completedResult.domains.map(
          ({ key, label, shortLabel, correct, total, percentage }) => ({
            key,
            label,
            shortLabel,
            correct,
            total,
            percentage,
          })
        ),
      };
      setResult(completedResult);
      setPhase("result");
      setReviewFilter("all");
      window.localStorage.removeItem(DRAFT_KEY);
      setResumeAvailable(false);
      saveHistory(completedResult);
      if (attemptId) {
        setTrackingStatus("saving");
        submitExamSimulationAttempt(EXAM_SIMULATION_SLUG, attemptId, serverResult)
          .then(() => setTrackingStatus("saved"))
          .catch(() => {
            queueExamSimulationSubmission({
              slug: EXAM_SIMULATION_SLUG,
              attemptId,
              result: serverResult,
            });
            setTrackingStatus("pending");
          });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [answers, attemptId, attemptSettings, flagged.length, questionBankVersion, questionIds, questionMap, saveHistory, startedAt]
  );

  useEffect(() => {
    if (phase !== "exam" || !endTime) return undefined;

    const updateTimer = () => {
      const nextRemaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setRemainingSeconds(nextRemaining);
      if (nextRemaining === 0) submitExam(true, true);
    };

    updateTimer();
    const timer = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(timer);
  }, [endTime, phase, submitExam]);

  useEffect(() => {
    if (phase !== "exam" || !questionIds.length) return;
    const draft = {
      version: questionBankVersion,
      questionIds,
      answers,
      flagged,
      currentIndex,
      startedAt,
      endTime,
      attemptSettings,
      attemptId,
    };
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (_) {
      // Autosave is a convenience; the active attempt can continue without it.
    }
  }, [answers, attemptId, attemptSettings, currentIndex, endTime, flagged, phase, questionBankVersion, questionIds, startedAt]);

  const startExam = async () => {
    setStartError("");
    setStartingAttempt(true);
    const now = Date.now();
    const nextAttemptSettings = { ...settings };
    const examSeconds = nextAttemptSettings.timeMinutes * 60;
    if (!questionBank.length || !questionBankVersion) {
      setStartError("The practice question bank is not available. Please refresh and try again.");
      setStartingAttempt(false);
      return;
    }
    const nextQuestionIds = selectClaudePracticeQuestionIds(
      questionBank,
      nextAttemptSettings
    );
    try {
      const started = await startExamSimulationAttempt(EXAM_SIMULATION_SLUG, {
        questionCount: nextQuestionIds.length,
        bankVersion: questionBankVersion,
      });
      setAttemptId(started.attemptId);
    } catch (error) {
      setStartError(
        error.message ||
          "The attempt could not be registered. Please try again."
      );
      setStartingAttempt(false);
      return;
    }
    setQuestionIds(nextQuestionIds);
    setAnswers({});
    setFlagged([]);
    setCurrentIndex(0);
    setStartedAt(now);
    setAttemptSettings(nextAttemptSettings);
    setEndTime(now + examSeconds * 1000);
    setRemainingSeconds(examSeconds);
    setResult(null);
    setTrackingStatus("");
    setPhase("exam");
    setStartingAttempt(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resumeExam = async () => {
    try {
      const draft = JSON.parse(window.localStorage.getItem(DRAFT_KEY) || "null");
      if (!draft || draft.version !== questionBankVersion || draft.endTime <= Date.now()) {
        window.localStorage.removeItem(DRAFT_KEY);
        setResumeAvailable(false);
        startExam();
        return;
      }
      let resumedAttemptId = draft.attemptId;
      if (!resumedAttemptId) {
        const started = await startExamSimulationAttempt(EXAM_SIMULATION_SLUG, {
          questionCount: draft.questionIds.length,
          bankVersion: questionBankVersion,
        });
        resumedAttemptId = started.attemptId;
      }
      setAttemptId(resumedAttemptId);
      setQuestionIds(draft.questionIds);
      setAnswers(draft.answers || {});
      setFlagged(draft.flagged || []);
      setCurrentIndex(Math.min(draft.currentIndex || 0, draft.questionIds.length - 1));
      setStartedAt(draft.startedAt);
      setEndTime(draft.endTime);
      setAttemptSettings(draft.attemptSettings || settings);
      setRemainingSeconds(Math.max(0, Math.ceil((draft.endTime - Date.now()) / 1000)));
      setPhase("exam");
    } catch (_) {
      startExam();
    }
  };

  const selectAnswer = (optionId) => {
    if (!activeQuestion) return;
    setAnswers((current) => {
      const selected = current[activeQuestion.id] || [];
      if (activeQuestion.type === "multiple") {
        return {
          ...current,
          [activeQuestion.id]: selected.includes(optionId)
            ? selected.filter((id) => id !== optionId)
            : [...selected, optionId],
        };
      }
      return { ...current, [activeQuestion.id]: [optionId] };
    });
  };

  const toggleFlag = () => {
    if (!activeQuestion) return;
    setFlagged((current) =>
      current.includes(activeQuestion.id)
        ? current.filter((id) => id !== activeQuestion.id)
        : [...current, activeQuestion.id]
    );
  };

  const returnToIntro = () => {
    setPhase("intro");
    setResult(null);
    setQuestionIds([]);
    setAnswers({});
    setFlagged([]);
    setCurrentIndex(0);
    setResumeAvailable(false);
    setAttemptId("");
    setTrackingStatus("");
  };

  if (phase === "intro") {
    return (
      <div className="claude-practice-page">
        <section className="claude-practice-hero">
          <div className="claude-practice-hero-copy">
            <span className="claude-practice-kicker">
              <Psychology fontSize="small" />
              UC Learning Lab
            </span>
            <h1>UC Claude Practice Test</h1>
            <p>
              A full-length readiness assessment aligned with the public blueprint for the
              Claude Certified Architect – Foundations certification.
            </p>
            <div className="claude-practice-facts" aria-label="Practice test details">
              <span><strong>{settings.questionCount}</strong> questions</span>
              <span><strong>{settings.timeMinutes}</strong> minutes</span>
              <span><strong>5</strong> domains</span>
              <span><strong>{settings.readinessScore}%</strong> UC readiness target</span>
            </div>
            <div className="claude-practice-actions">
              <button
                type="button"
                className="claude-primary-button"
                onClick={startExam}
                disabled={
                  !settingsLoaded ||
                  !questionBankLoaded ||
                  Boolean(questionBankError) ||
                  !settings.enabled ||
                  startingAttempt
                }
              >
                <School fontSize="small" />
                {!settingsLoaded || !questionBankLoaded
                  ? "Loading practice test"
                  : questionBankError
                    ? "Question bank unavailable"
                  : settings.enabled
                    ? startingAttempt
                      ? "Registering attempt"
                      : "Start new practice test"
                    : "Practice test is temporarily unavailable"}
              </button>
              {settings.enabled && resumeAvailable && (
                <button type="button" className="claude-secondary-button" onClick={resumeExam}>
                  Resume saved test
                </button>
              )}
            </div>
            {startError && <p className="claude-score-disclaimer">{startError}</p>}
            {questionBankError && (
              <p className="claude-score-disclaimer">{questionBankError}</p>
            )}
          </div>
          <div className="claude-practice-readiness-card">
            <span className="claude-practice-card-label">Recommended readiness standard</span>
            <strong>{settings.readinessScore}% overall</strong>
            <p>Plus at least {settings.domainMinimum}% in every domain on three unseen attempts.</p>
            <div className="claude-practice-card-note">
              UC practice results are not an Anthropic score or certification guarantee.
            </div>
          </div>
        </section>

        <section className="claude-practice-panel">
          <div className="claude-practice-section-heading">
            <span>Official blueprint alignment</span>
            <h2>What this practice test measures</h2>
          </div>
          <div className="claude-domain-list">
            {Object.entries(CLAUDE_EXAM_DOMAINS).map(([key, domain]) => (
              <div className="claude-domain-row" key={key}>
                <div>
                  <strong>{domain.label}</strong>
                  <small>{domain.weight}% of the official blueprint</small>
                </div>
                <div className="claude-domain-track" aria-hidden="true">
                  <span style={{ width: `${domain.weight * 3.2}%` }} />
                </div>
                <b>{questionBank.filter((item) => item.domain === key).length} Q</b>
              </div>
            ))}
          </div>
        </section>

        <section className="claude-practice-guidance">
          <div>
            <h2>Before you begin</h2>
            <ul>
              <li>Choose every correct response when a question says “Select” more than one.</li>
              <li>Your answers and remaining time are saved automatically in this browser.</li>
              <li>Use the flag control to mark questions for review before submitting.</li>
              <li>After submission, review explanations and repeat weak-domain study before another attempt.</li>
            </ul>
          </div>
          <button type="button" className="claude-link-button" onClick={() => navigate("/2.0")}>
            <ArrowBack fontSize="small" />
            Return to Udyog Connect
          </button>
        </section>
      </div>
    );
  }

  if (phase === "result" && result) {
    const reviewQuestions = result.questionIds
      .map((id) => questionMap.get(id))
      .filter(Boolean)
      .filter((item) => {
        if (reviewFilter === "all") return true;
        const correct = sameAnswers(result.answers[item.id] || [], item.correct);
        return reviewFilter === "correct" ? correct : !correct;
      });

    return (
      <div className="claude-practice-page">
        <section className={`claude-result-hero ${result.ready ? "ready" : "developing"}`}>
          <div>
            <span className="claude-practice-kicker">
              {result.ready ? <CheckCircle fontSize="small" /> : <Psychology fontSize="small" />}
              Practice test complete
            </span>
            <h1>{result.ready ? "UC readiness target achieved" : "Keep strengthening your weak domains"}</h1>
            <p>
              {result.timedOut
                ? "Time expired and the attempt was submitted automatically."
                : "Your attempt has been scored and saved in this browser."}
            </p>
          </div>
          <div className="claude-result-score">
            <strong>{result.percentage}%</strong>
            <span>{result.correctCount} of {result.total} correct</span>
          </div>
        </section>

        <section className="claude-practice-panel">
          <div className="claude-practice-section-heading">
            <span>Domain performance</span>
            <h2>Readiness requires balanced knowledge</h2>
          </div>
          <div className="claude-result-domains">
            {result.domains.map((domain) => (
              <article
                key={domain.key}
                className={domain.percentage >= result.domainMinimum ? "domain-pass" : "domain-review"}
              >
                <div>
                  <strong>{domain.shortLabel}</strong>
                  <small>{domain.correct} of {domain.total} correct</small>
                </div>
                <b>{domain.percentage}%</b>
                <div className="claude-result-domain-track">
                  <span style={{ width: `${domain.percentage}%` }} />
                </div>
              </article>
            ))}
          </div>
          <p className="claude-score-disclaimer">
            Anthropic reports a scaled score from 100–1,000 with a passing score of 720.
            This UC percentage is a preparation benchmark and is not a conversion of that scaled score.
          </p>
          <p className="claude-score-disclaimer">
            {trackingStatus === "saving" && "Saving this attempt to Exam Simulations…"}
            {trackingStatus === "saved" && "This attempt is available in the Exam Simulations Admin report."}
            {trackingStatus === "pending" && "The score is saved in this browser and will sync automatically when the connection returns."}
          </p>
        </section>

        <section className="claude-practice-panel">
          <div className="claude-review-toolbar">
            <div>
              <span>Answer review</span>
              <h2>Learn from every response</h2>
            </div>
            <div className="claude-review-filters" role="group" aria-label="Filter answer review">
              {[
                ["all", "All"],
                ["incorrect", "Needs review"],
                ["correct", "Correct"],
              ].map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className={reviewFilter === key ? "active" : ""}
                  onClick={() => setReviewFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="claude-answer-review">
            {reviewQuestions.map((item) => {
              const selected = result.answers[item.id] || [];
              const correct = sameAnswers(selected, item.correct);
              return (
                <article key={item.id} className={correct ? "answer-correct" : "answer-incorrect"}>
                  <div className="claude-answer-review-heading">
                    <span>{CLAUDE_EXAM_DOMAINS[item.domain].shortLabel} · Objective {item.objective}</span>
                    <b>{correct ? "Correct" : "Review"}</b>
                  </div>
                  <h3>{item.prompt}</h3>
                  <p><strong>Your answer:</strong> {selected.length ? selected.join(", ") : "Not answered"}</p>
                  <p><strong>Correct answer:</strong> {item.correct.join(", ")}</p>
                  <small>{item.explanation}</small>
                </article>
              );
            })}
          </div>
        </section>

        <div className="claude-result-actions">
          <button type="button" className="claude-primary-button" onClick={startExam}>
            <RestartAlt fontSize="small" />
            Start another attempt
          </button>
          <button type="button" className="claude-secondary-button" onClick={returnToIntro}>
            Back to test overview
          </button>
        </div>
      </div>
    );
  }

  if (!activeQuestion) return null;

  const selectedAnswers = answers[activeQuestion.id] || [];
  const isFlagged = flagged.includes(activeQuestion.id);

  return (
    <div className="claude-practice-page claude-exam-page">
      <header className="claude-exam-header">
        <div>
          <span>UC Claude Practice Test</span>
          <strong>Question {currentIndex + 1} of {questions.length}</strong>
        </div>
        <div className={`claude-exam-timer ${remainingSeconds <= 600 ? "warning" : ""}`}>
          <AccessTime fontSize="small" />
          <span>Time remaining</span>
          <strong>{formatTime(remainingSeconds)}</strong>
        </div>
      </header>

      <div className="claude-exam-progress" aria-label={`${answeredCount} of ${questions.length} answered`}>
        <span style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
      </div>

      <div className="claude-exam-layout">
        <aside className="claude-question-navigator" aria-label="Question navigator">
          <div className="claude-question-navigator-summary">
            <div><strong>{answeredCount}</strong><span>Answered</span></div>
            <div><strong>{questions.length - answeredCount}</strong><span>Remaining</span></div>
            <div><strong>{flagged.length}</strong><span>Flagged</span></div>
          </div>
          <div className="claude-question-grid">
            {questions.map((item, index) => {
              const answered = (answers[item.id] || []).length > 0;
              return (
                <button
                  type="button"
                  key={item.id}
                  className={[
                    index === currentIndex ? "current" : "",
                    answered ? "answered" : "",
                    flagged.includes(item.id) ? "flagged" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Question ${index + 1}${answered ? ", answered" : ""}${flagged.includes(item.id) ? ", flagged" : ""}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
          <button type="button" className="claude-submit-button" onClick={() => submitExam(false, false)}>
            Submit practice test
          </button>
        </aside>

        <main className="claude-question-card">
          <div className="claude-question-meta">
            <span>{CLAUDE_EXAM_DOMAINS[activeQuestion.domain].label}</span>
            <span>Objective {activeQuestion.objective}</span>
          </div>
          {activeQuestion.scenario && (
            <div className="claude-question-scenario">
              <strong>Scenario</strong>
              <p>{activeQuestion.scenario}</p>
            </div>
          )}
          <h1>{activeQuestion.prompt}</h1>
          <p className="claude-question-instruction">
            {activeQuestion.type === "multiple"
              ? `Multiple response · Select ${activeQuestion.correct.length} answers.`
              : "Select the best answer."}
          </p>

          <div className="claude-question-options">
            {activeQuestion.options.map((option) => {
              const selected = selectedAnswers.includes(option.id);
              return (
                <button
                  type="button"
                  key={option.id}
                  className={selected ? "selected" : ""}
                  onClick={() => selectAnswer(option.id)}
                  aria-pressed={selected}
                >
                  <span>{option.id}</span>
                  <p>{option.text}</p>
                  <i aria-hidden="true">{selected ? "✓" : ""}</i>
                </button>
              );
            })}
          </div>

          <div className="claude-question-actions">
            <button
              type="button"
              className={`claude-flag-button ${isFlagged ? "active" : ""}`}
              onClick={toggleFlag}
            >
              <Flag fontSize="small" />
              {isFlagged ? "Flagged for review" : "Flag for review"}
            </button>
            <div>
              <button
                type="button"
                className="claude-secondary-button"
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
              >
                <ArrowBack fontSize="small" />
                Previous
              </button>
              {currentIndex < questions.length - 1 ? (
                <button
                  type="button"
                  className="claude-primary-button"
                  onClick={() => setCurrentIndex((index) => Math.min(questions.length - 1, index + 1))}
                >
                  Next
                  <ArrowForward fontSize="small" />
                </button>
              ) : (
                <button type="button" className="claude-primary-button" onClick={() => submitExam(false, false)}>
                  Submit test
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClaudePracticeTest;
